// =============================================================================
// Lead submission + retrieval — talks to the funnel API (server/)
// -----------------------------------------------------------------------------
// The API persists every submission to SQLite and emails qualified applicants.
//
// API base URL:
//   • Production — leave REACT_APP_API_BASE_URL unset; nginx proxies /api/* to
//     the Node server, so same-origin requests work.
//   • Local dev  — the "proxy" field in package.json forwards /api/* to the
//     server on :3006, so same-origin requests work there too.
//   Override with REACT_APP_API_BASE_URL only for unusual hosting setups.
//
// If the API call fails, the submission is kept in localStorage as a backup so
// nothing is lost; see backupLocally().
// =============================================================================

import { FUNNEL_SOURCE } from './funnelConfig';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const SUBMIT_URL = `${API_BASE}/api/submit-lead`;
const LEADS_URL = `${API_BASE}/api/leads`;

const BACKUP_KEY = 'rt_careers_lead_backup';
const LAST_SUBMIT_KEY = 'rt_careers_last_submit';

// Reject a repeat submission of the same email within this window (ms).
const DUPLICATE_WINDOW_MS = 30 * 1000;

function normaliseEmail(email) {
  return (email || '').toLowerCase().trim();
}

/** True when this email was submitted moments ago — guards rapid re-submits. */
export function isDuplicateSubmission(email) {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(LAST_SUBMIT_KEY);
    if (!raw) return false;
    const { email: lastEmail, at } = JSON.parse(raw);
    return lastEmail === normaliseEmail(email) && Date.now() - at < DUPLICATE_WINDOW_MS;
  } catch (e) {
    return false;
  }
}

function markSubmission(email) {
  try {
    window.sessionStorage.setItem(
      LAST_SUBMIT_KEY,
      JSON.stringify({ email: normaliseEmail(email), at: Date.now() }),
    );
  } catch (e) {
    /* ignore */
  }
}

/** Keep a local copy when the API is unreachable, so a lead is never lost. */
function backupLocally(record) {
  try {
    const existing = JSON.parse(window.localStorage.getItem(BACKUP_KEY)) || [];
    existing.unshift(record);
    window.localStorage.setItem(BACKUP_KEY, JSON.stringify(existing));
  } catch (e) {
    /* localStorage unavailable — nothing more we can do client-side */
  }
}

/**
 * Persist a completed application via the funnel API.
 * @param {object} lead - all answers + score + tier + utm + attribution
 * @returns {Promise<object>} the submitted record plus `emailSent` — true only
 *   when the server confirmed the qualified-applicant email went out.
 */
export async function submitLead(lead) {
  const record = {
    id: `lead_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    source: FUNNEL_SOURCE,
    createdAt: new Date().toISOString(),
    ...lead,
  };

  let emailSent = false;
  try {
    const res = await fetch(SUBMIT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(record),
    });
    if (!res.ok) throw new Error(`Lead API responded ${res.status}`);
    const data = await res.json().catch(() => ({}));
    emailSent = Boolean(data.emailSent);
  } catch (err) {
    // Don't lose the submission — keep a local backup and warn.
    // eslint-disable-next-line no-console
    console.error('[leadStore] submission to API failed; kept a local backup', err);
    backupLocally(record);
  }

  markSubmission(record.email);
  return { ...record, emailSent };
}

/**
 * Fetch all leads for the admin review page.
 * @param {string} token - admin passcode, sent as a Bearer token
 * @returns {Promise<Array>} leads, newest first
 * @throws {Error} with `.code === 'unauthorized'` on a bad passcode
 */
export async function fetchLeads(token) {
  let res;
  try {
    res = await fetch(LEADS_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    throw new Error('Could not reach the server. Is the funnel API running?');
  }

  if (res.status === 401) {
    const err = new Error('Incorrect passcode.');
    err.code = 'unauthorized';
    throw err;
  }
  if (!res.ok) {
    throw new Error(`Server responded ${res.status}.`);
  }

  const data = await res.json().catch(() => ({}));
  return data.leads || [];
}
